/**
 * Review Page - Solution-Based Review with Diff View
 * 
 * 1 = "It's fine as is" (keep current)
 * 2-4 = Apply a solution
 * 5 = Escalate for deeper review
 * 0 = Dismiss (flag was wrong)
 */

import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Flag, ExternalLink, AlertTriangle, X, Plus, Minus } from 'lucide-react';
import { Link } from 'react-router-dom';
import ReactMarkdown from 'react-markdown';

const API_BASE = import.meta.env.VITE_API_URL ? `${import.meta.env.VITE_API_URL}/api` : '/api';

const getSectionTitle = (key) => ({
    description: 'Description', appeal: 'Why It Appeals',
    for_dominants: 'For Dominants', for_submissives: 'For Submissives',
    experience_levels: 'Experience Levels', risks: 'Risks & Safety',
    ethics: 'Ethics', activities: 'Activities',
    imaginary_scenarios: 'Scenarios', resources: 'Resources',
    media: 'Media', misconceptions: 'Misconceptions',
    history: 'History', glossary: 'Glossary',
    equipment: 'Equipment', physical_requirements: 'Physical Requirements'
}[key] || key);

const flagColors = {
    ai_detected: 'border-yellow-500', cringe: 'border-pink-500',
    inaccurate: 'border-red-500', missing: 'border-cyan-500',
    add_content: 'border-green-500', too_short: 'border-emerald-500',
    too_formal: 'border-purple-500', purple_prose: 'border-fuchsia-500',
    hedging: 'border-orange-500', other: 'border-dark-500'
};

const isAdditionFlag = (type) => ['missing', 'add_content', 'too_short'].includes(type);

// Simple line-based diff
function computeDiff(oldText, newText) {
    const oldLines = (oldText || '').split('\n');
    const newLines = (newText || '').split('\n');

    const oldSet = new Set(oldLines.map(l => l.trim()).filter(l => l));
    const newSet = new Set(newLines.map(l => l.trim()).filter(l => l));

    const result = [];

    // Find removed lines (in old but not new)
    for (const line of oldLines) {
        const trimmed = line.trim();
        if (trimmed && !newSet.has(trimmed)) {
            result.push({ type: 'removed', text: line });
        } else if (trimmed) {
            result.push({ type: 'same', text: line });
        }
    }

    // Find added lines (in new but not old)
    const added = [];
    for (const line of newLines) {
        const trimmed = line.trim();
        if (trimmed && !oldSet.has(trimmed)) {
            added.push({ type: 'added', text: line });
        }
    }

    return { result, added, hasChanges: added.length > 0 || result.some(r => r.type === 'removed') };
}

// Diff display component
function DiffView({ current, proposed }) {
    const diff = useMemo(() => computeDiff(current, proposed), [current, proposed]);

    if (!diff.hasChanges) {
        return (
            <div className="text-center py-8">
                <div className="text-yellow-500 text-lg mb-2">⚠️ No Changes</div>
                <p className="text-dark-400 text-sm">This solution is identical to current content</p>
            </div>
        );
    }

    return (
        <div className="space-y-1 font-mono text-sm">
            {/* Removed lines */}
            {diff.result.filter(r => r.type === 'removed').length > 0 && (
                <div className="mb-4">
                    <div className="text-xs text-red-400 uppercase tracking-wide mb-2 flex items-center gap-1">
                        <Minus className="w-3 h-3" /> Removed
                    </div>
                    {diff.result.filter(r => r.type === 'removed').map((line, i) => (
                        <div key={i} className="bg-red-950/40 border-l-2 border-red-500 pl-2 py-0.5 text-red-200">
                            {line.text || <span className="text-red-400 italic">(empty line)</span>}
                        </div>
                    ))}
                </div>
            )}

            {/* Added lines */}
            {diff.added.length > 0 && (
                <div>
                    <div className="text-xs text-green-400 uppercase tracking-wide mb-2 flex items-center gap-1">
                        <Plus className="w-3 h-3" /> Added
                    </div>
                    {diff.added.map((line, i) => (
                        <div key={i} className="bg-green-950/40 border-l-2 border-green-500 pl-2 py-0.5 text-green-200">
                            {line.text || <span className="text-green-400 italic">(empty line)</span>}
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
}

function ReviewPage() {
    const [flags, setFlags] = useState([]);
    const [currentIndex, setCurrentIndex] = useState(0);
    const [loading, setLoading] = useState(true);
    const [stats, setStats] = useState({ fixed: 0, kept: 0, escalated: 0, dismissed: 0 });
    const [hoveredSolution, setHoveredSolution] = useState(null);
    const [viewMode, setViewMode] = useState('diff'); // 'diff' or 'preview'

    const [feedbackNote, setFeedbackNote] = useState('');

    useEffect(() => { fetchFlags(); }, []);

    const fetchFlags = async () => {
        try {
            const res = await fetch(`${API_BASE}/kinks/flags?status=pending&limit=100`);
            const data = await res.json();
            setFlags(data.flags || []);
        } catch (err) { console.error('Failed to load:', err); }
        setLoading(false);
    };

    const currentFlag = flags[currentIndex];

    const handleSolution = useCallback(async (solutionNum) => {
        if (!currentFlag) return;
        try {
            await fetch(`${API_BASE}/kinks/flags/${currentFlag.id}`, {
                method: 'PATCH',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    solution: solutionNum,
                    reviewerFeedback: feedbackNote // Send the note
                })
            });

            const statKey = solutionNum === 0 ? 'dismissed'
                : solutionNum === 1 ? 'kept'
                    : solutionNum === 5 ? 'escalated'
                        : 'fixed';
            setStats(p => ({ ...p, [statKey]: p[statKey] + 1 }));
            setFlags(p => p.filter((_, i) => i !== currentIndex));
            // Reset note for next flag
            setFeedbackNote('');
            if (currentIndex >= flags.length - 1) setCurrentIndex(Math.max(0, currentIndex - 1));
            setHoveredSolution(null);
        } catch (err) { console.error('Failed:', err); }
    }, [currentFlag, currentIndex, flags.length, feedbackNote]); // Add feedbackNote to deps

    useEffect(() => {
        const handleKey = (e) => {
            // Don't trigger shortcuts when typing in the note field
            if (e.target.tagName === 'TEXTAREA' || e.target.tagName === 'INPUT') return;
            const key = e.key;
            if (['0', '1', '2', '3', '4', '5'].includes(key)) {
                const num = parseInt(key);
                if (num >= 2 && num <= 4) {
                    const solutions = currentFlag?.solutions || [];
                    if (solutions[num - 2]) handleSolution(num);
                } else {
                    handleSolution(num);
                }
            }
            // Toggle view mode with 'd' key
            if (key === 'd') {
                setViewMode(v => v === 'diff' ? 'preview' : 'diff');
            }
        };
        window.addEventListener('keydown', handleKey);
        return () => window.removeEventListener('keydown', handleKey);
    }, [handleSolution, currentFlag]);

    if (loading) return <div className="h-full flex items-center justify-center text-dark-400">Loading...</div>;

    if (!flags.length) return (
        <div className="h-full flex flex-col items-center justify-center gap-4">
            <div className="text-6xl">✓</div>
            <h2 className="text-2xl font-semibold text-dark-100">Queue empty</h2>
            <p className="text-dark-500 text-sm">
                {stats.fixed} fixed · {stats.kept} kept · {stats.escalated} escalated · {stats.dismissed} dismissed
            </p>
            <Link to="/" className="mt-4 px-4 py-2 bg-dark-800 rounded-lg text-dark-200">Back</Link>
        </div>
    );

    const solutions = currentFlag.solutions || [];
    const borderColor = flagColors[currentFlag.flagType] || flagColors.other;
    const hoveredContent = hoveredSolution >= 2 ? solutions[hoveredSolution - 2]?.content : null;

    return (
        <div className="h-full flex flex-col p-4 md:p-6 max-w-7xl mx-auto w-full">
            {/* Header */}
            <div className="flex items-center justify-between mb-6 flex-shrink-0">
                <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full bg-wine-500/10 flex items-center justify-center border border-wine-500/20">
                        <Flag className="w-5 h-5 text-wine-500" />
                    </div>
                    <div>
                        <div className="flex items-baseline gap-2">
                            <h1 className="font-display font-bold text-2xl text-gray-100">{currentFlag.kink?.name}</h1>
                            <span className="text-dark-500 text-sm">Reviewing</span>
                        </div>
                        <div className="flex items-center gap-2 text-sm">
                            <span className="text-wine-400 font-medium">{getSectionTitle(currentFlag.sectionKey)}</span>
                            <Link to={`/kink/${currentFlag.kinkId}#${currentFlag.sectionKey}`} target="_blank" className="text-dark-400 hover:text-white transition-colors">
                                <ExternalLink className="w-3 h-3" />
                            </Link>
                        </div>
                    </div>
                </div>
                <div className="flex items-center gap-4">
                    {/* View mode toggle */}
                    <div className="bg-dark-800 p-1 rounded-lg border border-dark-700 flex gap-1">
                        <button
                            onClick={() => setViewMode('diff')}
                            className={`px-3 py-1.5 rounded-md text-sm font-medium transition-all ${viewMode === 'diff' ? 'bg-wine-600 text-white shadow-lg' : 'text-gray-400 hover:text-gray-200'}`}
                        >
                            Diff (d)
                        </button>
                        <button
                            onClick={() => setViewMode('preview')}
                            className={`px-3 py-1.5 rounded-md text-sm font-medium transition-all ${viewMode === 'preview' ? 'bg-wine-600 text-white shadow-lg' : 'text-gray-400 hover:text-gray-200'}`}
                        >
                            Preview
                        </button>
                    </div>
                    <div className="text-right">
                        <div className="text-sm font-bold text-gray-200">{currentIndex + 1} / {flags.length}</div>
                        <div className="text-xs text-gray-500 font-mono">
                            {stats.fixed}✓ {stats.kept}= {stats.escalated}⚠ {stats.dismissed}✗
                        </div>
                    </div>
                </div>
            </div>

            {/* Main content */}
            <div className="flex-1 overflow-auto flex gap-4">
                {/* LEFT: Content area */}
                <div className={`flex-1 bg-dark-950 rounded-lg p-4 border-l-4 ${borderColor} overflow-auto`}>
                    {hoveredContent ? (
                        viewMode === 'diff' ? (
                            <>
                                <div className="text-xs text-blue-400 uppercase tracking-wide mb-3 font-medium">
                                    Changes in Solution {hoveredSolution}
                                </div>
                                <DiffView current={currentFlag.sectionContent || ''} proposed={hoveredContent} />
                            </>
                        ) : (
                            <>
                                <div className="text-xs text-green-500 uppercase tracking-wide mb-2 font-medium">
                                    Preview: Solution {hoveredSolution}
                                </div>
                                <div className="prose prose-invert prose-sm max-w-none">
                                    <ReactMarkdown>{hoveredContent}</ReactMarkdown>
                                </div>
                            </>
                        )
                    ) : currentFlag.sectionContent ? (
                        <>
                            <div className="text-xs text-dark-500 uppercase tracking-wide mb-2">Current Content</div>
                            <div className="prose prose-invert prose-sm max-w-none">
                                <ReactMarkdown>{currentFlag.sectionContent}</ReactMarkdown>
                            </div>
                        </>
                    ) : (
                        <p className="text-dark-500 italic">No content available (new section)</p>
                    )}
                </div>

                {/* RIGHT: Solutions panel */}
                <div className="w-96 flex-shrink-0 flex flex-col gap-2">
                    {/* Why flagged */}
                    <div className="bg-dark-900 rounded p-3 border border-dark-700">
                        <div className="text-xs text-dark-500 uppercase tracking-wide mb-1">Why flagged</div>
                        <p className="text-dark-200 text-sm">{currentFlag.note || 'No reason given'}</p>
                    </div>

                    {/* Example snippet - different styling for additions vs modifications */}
                    {currentFlag.example && (
                        <div className={isAdditionFlag(currentFlag.flagType)
                            ? "bg-green-950/30 rounded p-3 border border-green-900/50"
                            : "bg-red-950/30 rounded p-3 border border-red-900/50"
                        }>
                            <div className={`text-xs uppercase tracking-wide mb-1 ${isAdditionFlag(currentFlag.flagType) ? 'text-green-400' : 'text-red-400'
                                }`}>
                                {isAdditionFlag(currentFlag.flagType) ? "What's missing" : "Problematic text"}
                            </div>
                            <p className="text-dark-200 text-sm font-mono italic">"{currentFlag.example}"</p>
                        </div>
                    )}

                    {/* Option 1: Keep as is */}
                    <button
                        onClick={() => handleSolution(1)}
                        className="w-full text-left p-3 rounded-lg bg-dark-800 border border-dark-600 hover:border-blue-500 hover:bg-blue-500/5 transition-colors"
                    >
                        <div className="flex items-center gap-2">
                            <span className="w-6 h-6 rounded bg-blue-900/50 text-blue-400 flex items-center justify-center text-sm font-mono flex-shrink-0">1</span>
                            <span className="text-dark-200 text-sm">It's fine as is</span>
                        </div>
                    </button>

                    {/* Solutions 2-4 */}
                    <div className="flex-1 overflow-auto space-y-2">
                        {solutions.map((sol, i) => (
                            <button
                                key={i}
                                onClick={() => handleSolution(i + 2)}
                                onMouseEnter={() => setHoveredSolution(i + 2)}
                                onMouseLeave={() => setHoveredSolution(null)}
                                className="w-full text-left p-3 rounded-lg bg-dark-800 border border-dark-600 hover:border-green-500 hover:bg-green-500/5 transition-colors"
                            >
                                <div className="flex items-start gap-2">
                                    <span className="w-6 h-6 rounded bg-dark-700 text-dark-300 flex items-center justify-center text-sm font-mono flex-shrink-0">
                                        {i + 2}
                                    </span>
                                    <span className="text-dark-200 text-sm">{sol.label}</span>
                                </div>
                            </button>
                        ))}
                    </div>

                    {/* Feedback Input */}
                    <div className="pt-2 border-t border-dark-700">
                        <input
                            type="text"
                            value={feedbackNote}
                            onChange={(e) => setFeedbackNote(e.target.value)}
                            placeholder="Add note for agent (optional)..."
                            className="w-full bg-dark-900 border border-dark-700 rounded px-3 py-2 text-sm text-dark-200 placeholder-dark-500 focus:border-blue-500 focus:outline-none mb-2"
                        />
                    </div>

                    {/* Bottom actions */}
                    <div className="flex gap-2">
                        <button
                            onClick={() => handleSolution(0)}
                            className="flex-1 py-2 rounded bg-dark-800 border border-dark-600 text-dark-400 hover:border-dark-400 hover:text-dark-200 flex items-center justify-center gap-1 text-sm"
                        >
                            <X className="w-3 h-3" /> <span className="font-mono">0</span> Dismiss
                        </button>
                        <button
                            onClick={() => handleSolution(5)}
                            className="flex-1 py-2 rounded bg-dark-800 border border-dark-600 text-dark-400 hover:border-yellow-500 hover:text-yellow-400 flex items-center justify-center gap-1 text-sm"
                        >
                            <AlertTriangle className="w-3 h-3" /> <span className="font-mono">5</span> Escalate
                        </button>
                    </div>

                    <p className="text-center text-dark-600 text-xs mt-1">1 keep · 2-4 apply · 0 dismiss · 5 escalate · d toggle view</p>
                </div>
            </div>
        </div>
    );
}

export default ReviewPage;
